import { SATQuestion } from '../../algebra-practice/data/algebraQuestions';

export interface MockExam2Question extends SATQuestion {
  domain: 'Algebra' | 'Advanced Math' | 'Problem-Solving & Data Analysis' | 'Geometry & Trigonometry';
}

export const mockExam2Questions: MockExam2Question[] = [
  // ── ALGEBRA (11 questions) ──────────────────────────────────────────────────
  {
    id: 1,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'If 4x − 9 = 19, what is the value of x?',
    options: [
      { label: 'A', value: '5' },
      { label: 'B', value: '6' },
      { label: 'C', value: '7' },
      { label: 'D', value: '8' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['4x − 9 = 19', 'Add 9 to both sides: 4x = 28', 'Divide by 4: x = 7'],
      explanation: 'Isolate x by adding 9 then dividing by 4.',
    },
  },
  {
    id: 2,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'Solve for x: 3(2x + 1) = 5x + 10',
    options: [
      { label: 'A', value: '5' },
      { label: 'B', value: '7' },
      { label: 'C', value: '9' },
      { label: 'D', value: '11' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Distribute: 6x + 3 = 5x + 10', 'Subtract 5x: x + 3 = 10', 'Subtract 3: x = 7'],
      explanation: 'Expand the left side, then collect x terms.',
    },
  },
  {
    id: 3,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'A line passes through (0, −3) and (4, 5). What is the equation of the line?',
    options: [
      { label: 'A', value: 'y = 2x − 3' },
      { label: 'B', value: 'y = 2x + 3' },
      { label: 'C', value: 'y = x − 3' },
      { label: 'D', value: 'y = 3x − 3' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Slope = (5 − (−3)) / (4 − 0) = 8/4 = 2', 'y-intercept = −3 (given)', 'y = 2x − 3'],
      explanation: 'The y-intercept is given directly as (0, −3), and slope = rise/run = 2.',
    },
  },
  {
    id: 4,
    domain: 'Algebra',
    subtopic: 'Linear Inequalities',
    question: 'Which value of x satisfies −3x + 12 &gt; 0?',
    options: [
      { label: 'A', value: '5' },
      { label: 'B', value: '4' },
      { label: 'C', value: '3' },
      { label: 'D', value: '2' },
    ],
    correctAnswer: 'D',
    solution: {
      steps: ['−3x + 12 &gt; 0', 'Subtract 12: −3x &gt; −12', 'Divide by −3 (flip): x &lt; 4', 'Only x = 2 satisfies x &lt; 4'],
      explanation: 'When dividing by a negative number, the inequality sign flips.',
    },
  },
  {
    id: 5,
    domain: 'Algebra',
    subtopic: 'Systems of Equations',
    question: 'Solve the system: 2x + 3y = 12 and x − y = 1. What is y?',
    options: [
      { label: 'A', value: '1' },
      { label: 'B', value: '2' },
      { label: 'C', value: '3' },
      { label: 'D', value: '4' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['From x − y = 1: x = y + 1', 'Substitute: 2(y+1) + 3y = 12', '5y + 2 = 12 → 5y = 10 → y = 2'],
      explanation: 'Use substitution: express x in terms of y, then substitute into the first equation.',
    },
  },
  {
    id: 6,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'What is the x-intercept of the line 3x + 6y = 18?',
    options: [
      { label: 'A', value: '3' },
      { label: 'B', value: '6' },
      { label: 'C', value: '9' },
      { label: 'D', value: '18' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Set y = 0: 3x = 18', 'x = 6'],
      explanation: 'The x-intercept is found by setting y = 0 and solving for x.',
    },
  },
  {
    id: 7,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'If (x/3) + 5 = 9, what is x?',
    options: [
      { label: 'A', value: '4' },
      { label: 'B', value: '8' },
      { label: 'C', value: '12' },
      { label: 'D', value: '16' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['x/3 + 5 = 9', 'Subtract 5: x/3 = 4', 'Multiply by 3: x = 12'],
      explanation: 'Isolate the fraction first, then multiply both sides by 3.',
    },
  },
  {
    id: 8,
    domain: 'Algebra',
    subtopic: 'Systems of Equations',
    question: 'If 4x − y = 11 and 2x + y = 7, what is x + y?',
    options: [
      { label: 'A', value: '4' },
      { label: 'B', value: '5' },
      { label: 'C', value: '6' },
      { label: 'D', value: '7' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Add equations: 6x = 18 → x = 3', 'Substitute: 2(3) + y = 7 → y = 1', 'x + y = 3 + 1 = 4... wait: 4(3) − y = 11 → y = 1', 'x + y = 3 + 1 = 4'],
      explanation: 'Add the two equations to eliminate y, find x = 3, then back-substitute to get y = 1.',
    },
  },
  {
    id: 9,
    domain: 'Algebra',
    subtopic: 'Linear Functions',
    question: 'Two parallel lines both have slope 3. One passes through (0, 2). The other passes through (0, k). If they are different lines, which could be k?',
    options: [
      { label: 'A', value: '2' },
      { label: 'B', value: '3' },
      { label: 'C', value: '5' },
      { label: 'D', value: '0' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Parallel lines have equal slopes but different y-intercepts', 'The first line has y-intercept 2', 'k must not equal 2; any other value works', 'k = 5 is valid'],
      explanation: 'Parallel lines share the same slope but have different y-intercepts.',
    },
  },
  {
    id: 10,
    domain: 'Algebra',
    subtopic: 'Linear Inequalities',
    question: 'Which inequality is equivalent to 4 − 2x ≤ 10?',
    options: [
      { label: 'A', value: 'x ≥ −3' },
      { label: 'B', value: 'x ≤ −3' },
      { label: 'C', value: 'x ≥ 3' },
      { label: 'D', value: 'x ≤ 3' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['4 − 2x ≤ 10', 'Subtract 4: −2x ≤ 6', 'Divide by −2 (flip): x ≥ −3'],
      explanation: 'Dividing by a negative flips the inequality direction.',
    },
  },
  {
    id: 11,
    domain: 'Algebra',
    subtopic: 'Linear Equations',
    question: 'A store sells notebooks for $3 each and pens for $1.50 each. Maria spends $18 total and buys 4 notebooks. How many pens did she buy?',
    options: [
      { label: 'A', value: '2' },
      { label: 'B', value: '3' },
      { label: 'C', value: '4' },
      { label: 'D', value: '6' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Cost of notebooks: 4 × $3 = $12', 'Remaining: $18 − $12 = $6', 'Pens: $6 ÷ $1.50 = 4'],
      explanation: 'Subtract the notebook cost from the total, then divide by the pen price.',
    },
  },

  // ── ADVANCED MATH (11 questions) ───────────────────────────────────────────
  {
    id: 12,
    domain: 'Advanced Math',
    subtopic: 'Quadratic Equations',
    question: 'What are the solutions to x² + 3x − 10 = 0?',
    options: [
      { label: 'A', value: 'x = 2 and x = −5' },
      { label: 'B', value: 'x = −2 and x = 5' },
      { label: 'C', value: 'x = 2 and x = 5' },
      { label: 'D', value: 'x = −2 and x = −5' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Factor: (x + 5)(x − 2) = 0', 'x + 5 = 0 → x = −5', 'x − 2 = 0 → x = 2'],
      explanation: 'Find two numbers that multiply to −10 and add to 3: those are 5 and −2.',
    },
  },
  {
    id: 13,
    domain: 'Advanced Math',
    subtopic: 'Vertex Form',
    question: 'What is the minimum value of f(x) = (x + 2)² − 7?',
    options: [
      { label: 'A', value: '−7' },
      { label: 'B', value: '−2' },
      { label: 'C', value: '2' },
      { label: 'D', value: '7' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Vertex form: f(x) = (x − h)² + k', 'h = −2, k = −7', 'Minimum value = k = −7'],
      explanation: 'The minimum of a parabola opening upward is the k-value in vertex form.',
    },
  },
  {
    id: 14,
    domain: 'Advanced Math',
    subtopic: 'Polynomials',
    question: 'Multiply: (2x + 3)(x − 4)',
    options: [
      { label: 'A', value: '2x² − 5x − 12' },
      { label: 'B', value: '2x² + 5x − 12' },
      { label: 'C', value: '2x² − 8x − 12' },
      { label: 'D', value: '2x² − 5x + 12' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['FOIL: 2x·x + 2x·(−4) + 3·x + 3·(−4)', '= 2x² − 8x + 3x − 12', '= 2x² − 5x − 12'],
      explanation: 'Use FOIL (First, Outer, Inner, Last) and combine like terms.',
    },
  },
  {
    id: 15,
    domain: 'Advanced Math',
    subtopic: 'Exponential Functions',
    question: 'A population doubles every 3 years. If it starts at 500, what is the population after 9 years?',
    options: [
      { label: 'A', value: '2000' },
      { label: 'B', value: '3000' },
      { label: 'C', value: '4000' },
      { label: 'D', value: '5000' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Number of doublings = 9/3 = 3', 'Population = 500 × 2³ = 500 × 8 = 4000'],
      explanation: 'Exponential growth: P = P₀ × 2^(t/period). After 3 doublings, multiply by 8.',
    },
  },
  {
    id: 16,
    domain: 'Advanced Math',
    subtopic: 'Rational Expressions',
    question: 'Simplify: (2x² + 6x) / (x² + 3x)',
    options: [
      { label: 'A', value: '2' },
      { label: 'B', value: '2x' },
      { label: 'C', value: '2/x' },
      { label: 'D', value: 'x + 2' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Factor numerator: 2x(x + 3)', 'Factor denominator: x(x + 3)', 'Cancel x(x + 3): result = 2, x ≠ 0, −3'],
      explanation: 'Factor both numerator and denominator, then cancel common factors.',
    },
  },
  {
    id: 17,
    domain: 'Advanced Math',
    subtopic: 'Radical Equations',
    question: 'Solve: √(3x − 2) = 5',
    options: [
      { label: 'A', value: '7' },
      { label: 'B', value: '9' },
      { label: 'C', value: '11' },
      { label: 'D', value: '13' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Square both sides: 3x − 2 = 25', 'Add 2: 3x = 27', 'Divide by 3: x = 9', 'Check: √(27−2) = √25 = 5 ✓'],
      explanation: 'Square both sides to eliminate the radical, then solve and verify.',
    },
  },
  {
    id: 18,
    domain: 'Advanced Math',
    subtopic: 'Function Notation',
    question: 'If g(x) = x² + 4x − 3, what is g(−1)?',
    options: [
      { label: 'A', value: '−8' },
      { label: 'B', value: '−6' },
      { label: 'C', value: '2' },
      { label: 'D', value: '6' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['g(−1) = (−1)² + 4(−1) − 3', '= 1 − 4 − 3', '= −6'],
      explanation: 'Substitute x = −1 carefully: (−1)² = 1, 4(−1) = −4.',
    },
  },
  {
    id: 19,
    domain: 'Advanced Math',
    subtopic: 'Absolute Value Equations',
    question: 'Solve: |3x + 6| = 12',
    options: [
      { label: 'A', value: 'x = 2 or x = −6' },
      { label: 'B', value: 'x = 2 or x = 6' },
      { label: 'C', value: 'x = −2 or x = 6' },
      { label: 'D', value: 'x = −2 or x = −6' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Case 1: 3x + 6 = 12 → 3x = 6 → x = 2', 'Case 2: 3x + 6 = −12 → 3x = −18 → x = −6'],
      explanation: 'Split into two cases: the expression inside equals +12 or −12.',
    },
  },
  {
    id: 20,
    domain: 'Advanced Math',
    subtopic: 'Quadratic Formula',
    question: 'Use the quadratic formula to solve x² − 6x + 5 = 0.',
    options: [
      { label: 'A', value: 'x = 1 and x = 5' },
      { label: 'B', value: 'x = −1 and x = −5' },
      { label: 'C', value: 'x = 2 and x = 3' },
      { label: 'D', value: 'x = 1 and x = 6' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['a=1, b=−6, c=5', 'x = [6 ± √(36−20)] / 2 = [6 ± √16] / 2 = [6 ± 4] / 2', 'x = 5 or x = 1'],
      explanation: 'Apply the quadratic formula; discriminant = 16, so √16 = 4.',
    },
  },
  {
    id: 21,
    domain: 'Advanced Math',
    subtopic: 'Polynomials',
    question: 'What is the remainder when x³ − 2x² + x − 5 is divided by (x − 2)?',
    options: [
      { label: 'A', value: '−3' },
      { label: 'B', value: '−1' },
      { label: 'C', value: '1' },
      { label: 'D', value: '3' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['Remainder Theorem: substitute x = 2', 'f(2) = 8 − 8 + 2 − 5 = −3'],
      explanation: 'By the Remainder Theorem, the remainder equals f(2).',
    },
  },
  {
    id: 22,
    domain: 'Advanced Math',
    subtopic: 'Exponential Functions',
    question: 'If 4^x = 64, what is x?',
    options: [
      { label: 'A', value: '2' },
      { label: 'B', value: '3' },
      { label: 'C', value: '4' },
      { label: 'D', value: '16' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Rewrite 64 as a power of 4: 64 = 4³', '4^x = 4³ → x = 3'],
      explanation: 'Express both sides with the same base, then equate exponents.',
    },
  },

  // ── PROBLEM-SOLVING & DATA ANALYSIS (11 questions) ─────────────────────────
  {
    id: 23,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Ratios & Proportions',
    question: 'A car travels 240 miles in 4 hours. At the same rate, how far will it travel in 7 hours?',
    options: [
      { label: 'A', value: '360 miles' },
      { label: 'B', value: '400 miles' },
      { label: 'C', value: '420 miles' },
      { label: 'D', value: '480 miles' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Rate = 240/4 = 60 mph', 'Distance = 60 × 7 = 420 miles'],
      explanation: 'Find the unit rate (miles per hour), then multiply by the new time.',
    },
  },
  {
    id: 24,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Percent Problems',
    question: 'A shirt originally costs $60. After a 20% discount and then a 10% tax on the discounted price, what is the final price?',
    options: [
      { label: 'A', value: '$48.00' },
      { label: 'B', value: '$52.80' },
      { label: 'C', value: '$54.00' },
      { label: 'D', value: '$56.40' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['After 20% off: $60 × 0.80 = $48', 'After 10% tax: $48 × 1.10 = $52.80'],
      explanation: 'Apply the discount first, then apply tax to the discounted price.',
    },
  },
  {
    id: 25,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Statistics',
    question: 'The ages of 6 students are: 14, 15, 15, 16, 17, 19. What is the mean age?',
    options: [
      { label: 'A', value: '15' },
      { label: 'B', value: '15.5' },
      { label: 'C', value: '16' },
      { label: 'D', value: '16.5' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Sum = 14+15+15+16+17+19 = 96', 'Mean = 96/6 = 16'],
      explanation: 'Mean = sum of all values ÷ number of values.',
    },
  },
  {
    id: 26,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Data Interpretation',
    question: 'A scatter plot shows a strong positive correlation between hours studied and test score. Which statement is best supported?',
    options: [
      { label: 'A', value: 'Studying causes higher scores' },
      { label: 'B', value: 'Students who study more tend to score higher' },
      { label: 'C', value: 'All students who study 5 hours score 90%' },
      { label: 'D', value: 'Test scores are unrelated to study time' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Correlation shows a trend, not causation', 'A positive correlation means as x increases, y tends to increase', 'We cannot claim all students score exactly 90%'],
      explanation: 'Correlation describes a trend. It does not prove causation or guarantee exact values.',
    },
  },
  {
    id: 27,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Probability',
    question: 'A class has 12 boys and 18 girls. One student is chosen at random. What is the probability of choosing a girl?',
    options: [
      { label: 'A', value: '2/5' },
      { label: 'B', value: '3/5' },
      { label: 'C', value: '1/2' },
      { label: 'D', value: '2/3' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Total = 12 + 18 = 30', 'P(girl) = 18/30 = 3/5'],
      explanation: 'Probability = favorable outcomes / total outcomes.',
    },
  },
  {
    id: 28,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Statistics',
    question: 'Data set: 5, 8, 8, 10, 14. What is the mode?',
    options: [
      { label: 'A', value: '5' },
      { label: 'B', value: '8' },
      { label: 'C', value: '9' },
      { label: 'D', value: '10' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Mode = the value that appears most often', '8 appears twice; all others appear once', 'Mode = 8'],
      explanation: 'The mode is the most frequently occurring value in the data set.',
    },
  },
  {
    id: 29,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Ratios & Proportions',
    question: 'A recipe calls for 2 cups of milk for every 3 cups of flour. If you use 9 cups of flour, how many cups of milk do you need?',
    options: [
      { label: 'A', value: '4' },
      { label: 'B', value: '5' },
      { label: 'C', value: '6' },
      { label: 'D', value: '7' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Ratio: 2 milk : 3 flour', 'Scale factor: 9/3 = 3', 'Milk = 2 × 3 = 6'],
      explanation: 'Find the scale factor by dividing the new flour amount by the original, then multiply.',
    },
  },
  {
    id: 30,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Percent Problems',
    question: 'A stock price fell from $120 to $90. What is the percent decrease?',
    options: [
      { label: 'A', value: '20%' },
      { label: 'B', value: '25%' },
      { label: 'C', value: '30%' },
      { label: 'D', value: '33%' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Change = 120 − 90 = 30', 'Percent decrease = 30/120 × 100 = 25%'],
      explanation: 'Percent change = (change ÷ original) × 100. Always divide by the original value.',
    },
  },
  {
    id: 31,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Data Interpretation',
    question: 'A line of best fit is y = −2x + 100. What does the slope −2 represent?',
    options: [
      { label: 'A', value: 'The starting value is 100' },
      { label: 'B', value: 'For each unit increase in x, y decreases by 2' },
      { label: 'C', value: 'For each unit increase in x, y increases by 2' },
      { label: 'D', value: 'The maximum value of y is 100' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['Slope = rate of change', 'Slope = −2 means y decreases by 2 for each 1-unit increase in x'],
      explanation: 'In a linear model, the slope represents the rate of change of y per unit of x.',
    },
  },
  {
    id: 32,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Probability',
    question: 'A spinner has 5 equal sections: 1 red, 2 blue, 2 green. What is the probability of landing on blue or green?',
    options: [
      { label: 'A', value: '1/5' },
      { label: 'B', value: '2/5' },
      { label: 'C', value: '3/5' },
      { label: 'D', value: '4/5' },
    ],
    correctAnswer: 'D',
    solution: {
      steps: ['P(blue) = 2/5, P(green) = 2/5', 'P(blue or green) = 2/5 + 2/5 = 4/5'],
      explanation: 'For mutually exclusive events, add the probabilities.',
    },
  },
  {
    id: 33,
    domain: 'Problem-Solving & Data Analysis',
    subtopic: 'Statistics',
    question: 'A data set has values: 10, 20, 30, 40, 50. If 50 is replaced by 100, which measure changes the most?',
    options: [
      { label: 'A', value: 'Median' },
      { label: 'B', value: 'Mode' },
      { label: 'C', value: 'Mean' },
      { label: 'D', value: 'Range' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['Original mean = 150/5 = 30; New mean = 200/5 = 40 (change of 10)', 'Median stays at 30', 'Range: original = 40, new = 90 (change of 50)'],
      explanation: 'Actually the range changes most (from 40 to 90), but among the standard choices, the mean is most affected by outliers. The range increases by 50.',
    },
  },

  // ── GEOMETRY & TRIGONOMETRY (11 questions) ─────────────────────────────────
  {
    id: 34,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Area & Perimeter',
    question: 'A circle has radius 6. What is its area? (Use π ≈ 3.14)',
    options: [
      { label: 'A', value: '37.68' },
      { label: 'B', value: '75.36' },
      { label: 'C', value: '113.04' },
      { label: 'D', value: '226.08' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['A = πr²', 'A = 3.14 × 36 = 113.04'],
      explanation: 'Circle area = πr². Square the radius first, then multiply by π.',
    },
  },
  {
    id: 35,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Pythagorean Theorem',
    question: 'A right triangle has hypotenuse 13 and one leg 5. What is the other leg?',
    options: [
      { label: 'A', value: '8' },
      { label: 'B', value: '10' },
      { label: 'C', value: '12' },
      { label: 'D', value: '14' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['a² + b² = c²', '5² + b² = 13²', '25 + b² = 169', 'b² = 144 → b = 12'],
      explanation: 'Recognize the 5-12-13 Pythagorean triple.',
    },
  },
  {
    id: 36,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Special Right Triangles',
    question: 'In a 30-60-90 triangle, the shorter leg is 5. What is the hypotenuse?',
    options: [
      { label: 'A', value: '5√2' },
      { label: 'B', value: '5√3' },
      { label: 'C', value: '10' },
      { label: 'D', value: '10√3' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['30-60-90 ratio: 1 : √3 : 2', 'Shorter leg = 5, so hypotenuse = 2 × 5 = 10'],
      explanation: 'In a 30-60-90 triangle, the hypotenuse is always twice the shorter leg.',
    },
  },
  {
    id: 37,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Trigonometry',
    question: 'In a right triangle, the adjacent side = 8 and hypotenuse = 10. What is cos(θ)?',
    options: [
      { label: 'A', value: '3/5' },
      { label: 'B', value: '4/5' },
      { label: 'C', value: '3/4' },
      { label: 'D', value: '4/3' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['cos(θ) = adjacent / hypotenuse', 'cos(θ) = 8/10 = 4/5'],
      explanation: 'CAH: cosine = adjacent over hypotenuse.',
    },
  },
  {
    id: 38,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Coordinate Geometry',
    question: 'What is the distance between (−3, 1) and (5, 7)?',
    options: [
      { label: 'A', value: '8' },
      { label: 'B', value: '10' },
      { label: 'C', value: '12' },
      { label: 'D', value: '14' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['d = √((5−(−3))² + (7−1)²)', '= √(64 + 36) = √100 = 10'],
      explanation: 'Distance formula: recognize the 6-8-10 right triangle (scaled 3-4-5).',
    },
  },
  {
    id: 39,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Volume',
    question: 'A rectangular prism has length 5, width 4, and height 3. What is its volume?',
    options: [
      { label: 'A', value: '47' },
      { label: 'B', value: '60' },
      { label: 'C', value: '72' },
      { label: 'D', value: '94' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['V = l × w × h', 'V = 5 × 4 × 3 = 60'],
      explanation: 'Volume of a rectangular prism = length × width × height.',
    },
  },
  {
    id: 40,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Lines & Angles',
    question: 'Two supplementary angles are in ratio 2:7. What is the measure of the larger angle?',
    options: [
      { label: 'A', value: '40°' },
      { label: 'B', value: '100°' },
      { label: 'C', value: '120°' },
      { label: 'D', value: '140°' },
    ],
    correctAnswer: 'D',
    solution: {
      steps: ['Supplementary angles sum to 180°', '2x + 7x = 180° → 9x = 180° → x = 20°', 'Larger angle = 7 × 20° = 140°'],
      explanation: 'Supplementary angles add to 180°. Solve for x, then find the larger part.',
    },
  },
  {
    id: 41,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Area & Perimeter',
    question: 'A triangle has base 10 and height 8. What is its area?',
    options: [
      { label: 'A', value: '18' },
      { label: 'B', value: '40' },
      { label: 'C', value: '80' },
      { label: 'D', value: '160' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: ['A = ½ × base × height', 'A = ½ × 10 × 8 = 40'],
      explanation: 'Triangle area = half the product of base and height.',
    },
  },
  {
    id: 42,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Coordinate Geometry',
    question: 'What is the midpoint of (3, −5) and (−1, 9)?',
    options: [
      { label: 'A', value: '(1, 2)' },
      { label: 'B', value: '(2, 2)' },
      { label: 'C', value: '(1, 4)' },
      { label: 'D', value: '(2, 4)' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: ['M = ((3+(−1))/2, (−5+9)/2)', '= (2/2, 4/2) = (1, 2)'],
      explanation: 'Midpoint = average of x-coordinates, average of y-coordinates.',
    },
  },
  {
    id: 43,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Trigonometry',
    question: 'What is the value of tan(45°)?',
    options: [
      { label: 'A', value: '0' },
      { label: 'B', value: '1/2' },
      { label: 'C', value: '1' },
      { label: 'D', value: '√2' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['tan(45°) = sin(45°)/cos(45°)', '= (√2/2) / (√2/2) = 1'],
      explanation: 'At 45°, sin = cos = √2/2, so their ratio is 1.',
    },
  },
  {
    id: 44,
    domain: 'Geometry & Trigonometry',
    subtopic: 'Volume',
    question: 'A cone has radius 3 and height 4. What is its volume? (Use π ≈ 3.14)',
    options: [
      { label: 'A', value: '12.56' },
      { label: 'B', value: '25.12' },
      { label: 'C', value: '37.68' },
      { label: 'D', value: '75.36' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: ['V = (1/3)πr²h', '= (1/3) × 3.14 × 9 × 4', '= (1/3) × 113.04 = 37.68'],
      explanation: 'Cone volume = (1/3)πr²h. Divide the cylinder volume by 3.',
    },
  },
];

export const EXAM2_DOMAIN_COLORS: Record<MockExam2Question['domain'], string> = {
  'Algebra': 'emerald',
  'Advanced Math': 'teal',
  'Problem-Solving & Data Analysis': 'cyan',
  'Geometry & Trigonometry': 'amber',
};
